<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import LeResultsListItem from "@/components/dictionary-selector/LeResultsListItem.vue";
import { useCompanyDictionaryAd, useDictionarySelector } from "@/composables";
import { MatomoHelper } from "@/helpers";
import { useUserStore } from "@/stores";
import { type Dictionary, MatomoEventAction, MatomoEventCategory, type SearchViewType } from "@/types";

const props = defineProps<{
  searchViewType: SearchViewType;
}>();

const { t } = useI18n();
const { userData } = storeToRefs(useUserStore());

const { dictionaryId, isAdSelected } = useCompanyDictionaryAd();
const { onSearchInDictionary } = useDictionarySelector();

const companyName = ref<string>(userData.value.companyName);

const dictionary = computed<Dictionary>(() => ({
  id: dictionaryId,
  title: companyName.value,
  publisher: t("le-company-dictionary-ad-button.text"),

  // Necessary properties, but not used for this fake dictionary
  subtitle: "",
  publisherKey: "",
  publisherLetter: null,
  brandColor: null,
  groupHeading: null,
  isbns: [],
  hasAbout: false,
  description: "",
  intro: "",
  searchPriority: 0,
  methods: [],
  tags: [],
  metadata: [],
  auto: false,
  isUpsell: false
}));

const onSelect = () => {
  onSearchInDictionary(dictionaryId);

  MatomoHelper.sendEvent(
    MatomoEventCategory.CompanyDictionaryAd,
    MatomoEventAction.ClickGetMoreInformation,
    `Dictionary list item was clicked [${props.searchViewType}]`
  );
};
</script>

<template>
  <div class="le-company-dictionary-ad-button">
    <h3 class="le-company-dictionary-ad-button__header">
      {{ t("le-company-dictionary-ad-button.title") }}
    </h3>
    <div class="le-company-dictionary-ad-button__content">
      <LeResultsListItem
        class="le-company-dictionary-ad-button__item"
        :dictionary="dictionary"
        :is-selected="isAdSelected"
        @select="onSelect()"
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-company-dictionary-ad-button {
  &__header {
    margin-top: 0px;
    margin-bottom: 12px;
    color: @colors[@neutrals][500];
    font-family: @font-family-heading;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
  }

  &__content {
    border-radius: 4px;
    box-shadow: @shadows[dropdown];
  }

  &__item {
    border-radius: 4px;
  }

  :deep(.le-results-list-button) {
    background-color: @colors[@primary][100];

    &:hover {
      background-color: @colors[@secondary][200];
    }

    &--selected {
      background-color: @colors[@accent][300];

      &:hover {
        background-color: @colors[@accent][400];
      }
    }
  }
}
</style>
